// Admin Service - Handles formulary and pharmacy CRUD operations using HATEOAS pattern
import type { Api } from '../types/generated'
import type { FormularyItem, FormularyListResponse, FormularyQueryParams } from './formularyService'
import { getApiConfiguration } from '../config/apiConfig'

// TypeScript interfaces for admin operations
// Using generated model types from Api namespace
export type Resource = Api.Resource

export type Formulary = Api.Formulary

export type PharmacyPostRequest = Api.PharmacyPostRequest

export type PharmacyPatchRequest = Api.PharmacyPatchRequest

export type PharmacyFormularyPutRequest = Api.PharmacyFormularyPutRequest

export type PharmacyFormularyList = Api.PharmacyFormularyList

export type PharmacyFormularyGet200Response = Api.PharmacyFormularyGet200Response

export interface FormularyCreateRequest {
  name: string
  category: string
  form: string
  route: string
  quantity: number
  unit: string
}

export interface FormularyUpdateRequest {
  id: string
  name?: string
  category?: string
  form?: string
  route?: string
  quantity?: number
  unit?: string
}

export interface PharmacyCreateRequest {
  name: string
  address: {
    street: string[]
    city: string
    state: string
    postal_code: string
    country: string
  }
  phone?: string
  email?: string
}

export interface PharmacyUpdateRequest {
  id: string
  name?: string
  address?: {
    street?: string[]
    city?: string
    state?: string
    postal_code?: string
    country?: string
  }
  phone?: string
  email?: string
}

export interface PricingUpdateRequest {
  pharmacyId: string
  formularyId: string
  price: number
  currency?: string
}

// Use existing formulary interfaces from formularyService
export type AdminFormularyListResponse = FormularyListResponse
export type AdminFormularyItem = FormularyItem

export interface PharmacyListItem {
  id: string
  href: string
  name: string
  address: {
    street: string[]
    city: string
    state: string
    postal_code: string
    country: string
  }
  phone?: string
  email?: string
  formularies_href?: string
}

export interface AdminPharmacyListResponse {
  page_size: number
  page_number: number
  page_previous: string | null
  page_next: string | null
  total_items: number
  href: string
  items: PharmacyListItem[]
}

export const adminApi = {
  // Formulary Management
  async getFormularies(params?: FormularyQueryParams): Promise<AdminFormularyListResponse> {
    // Make direct fetch call to /formulary endpoint with query parameters
    const config = getApiConfiguration()
    const url = new URL(`${config.basePath}/formulary`)
    
    // Add query parameters
    if (params) {
      if (params.category) params.category.forEach(c => url.searchParams.append('category', c))
      if (params.form) params.form.forEach(f => url.searchParams.append('form', f))
      if (params.route) params.route.forEach(r => url.searchParams.append('route', r))
      if (params.pharmacy) params.pharmacy.forEach(p => url.searchParams.append('pharmacy', p))
      if (params.search) params.search.forEach(s => url.searchParams.append('search', s))
      if (params.page_size) url.searchParams.set('page_size', params.page_size.toString())
      if (params.page_number) url.searchParams.set('page_number', params.page_number.toString())
    }
    
    const response = await fetch(url.toString(), {
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      }
    })
    
    if (!response.ok) {
      throw new Error(`Failed to fetch formularies: ${response.statusText}`)
    }
    
    return response.json()
  },

  async createFormulary(request: FormularyCreateRequest): Promise<Api.Resource> {
    const config = getApiConfiguration()
    const url = `${config.basePath}/formulary`
    
    const formularyData: Api.Formulary = {
      form: request.form as Api.Formulary['form'],
      route: request.route as Api.Formulary['route'],
      category: request.category as Api.Formulary['category'],
      name: request.name,
      quantity: request.quantity,
      unit: request.unit,
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formularyData)
    })
    
    if (!response.ok) {
      throw new Error(`Failed to create formulary: ${response.statusText}`)
    }
    
    return await response.json()
  },

  async updateFormulary(request: FormularyUpdateRequest): Promise<Api.Resource> {
    const config = getApiConfiguration()
    const url = `${config.basePath}/formulary/${request.id}`
    
    const formularyData: Partial<Api.Formulary> = {}
    if (request.form) formularyData.form = request.form as Api.Formulary['form']
    if (request.route) formularyData.route = request.route as Api.Formulary['route']
    if (request.category) formularyData.category = request.category as Api.Formulary['category']
    if (request.name) formularyData.name = request.name
    if (request.quantity) formularyData.quantity = request.quantity
    if (request.unit) formularyData.unit = request.unit
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formularyData)
    })
    
    if (!response.ok) {
      throw new Error(`Failed to update formulary: ${response.statusText}`)
    }
    
    return await response.json()
  },

  async deleteFormulary(id: string): Promise<void> {
    const config = getApiConfiguration()
    const url = `${config.basePath}/formulary/${id}`
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      }
    })
    
    if (!response.ok) {
      throw new Error(`Failed to delete formulary: ${response.statusText}`)
    }
  },

  // Pharmacy Management
  async getPharmacies(params?: {
    page_size?: number
    page_number?: number
    name?: string[]
  }): Promise<AdminPharmacyListResponse> {
    const config = getApiConfiguration()
    const url = new URL(`${config.basePath}/pharmacy`)
    
    if (params) {
      if (params.name) params.name.forEach(n => url.searchParams.append('name', n))
      if (params.page_size) url.searchParams.set('page_size', params.page_size.toString())
      if (params.page_number) url.searchParams.set('page_number', params.page_number.toString())
    }
    
    const response = await fetch(url.toString(), {
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      }
    })
    
    if (!response.ok) {
      throw new Error(`Failed to fetch pharmacies: ${response.statusText}`)
    }
    
    return await response.json()
  },

  async createPharmacy(request: PharmacyCreateRequest): Promise<Api.Resource> {
    const config = getApiConfiguration()
    const url = `${config.basePath}/pharmacy`
    
    const pharmacyData: Api.PharmacyPostRequest = {
      name: request.name as Api.PharmacyPostRequest['name'],
      address: request.address,
      phone: request.phone,
      email: request.email,
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(pharmacyData)
    })
    
    if (!response.ok) {
      throw new Error(`Failed to create pharmacy: ${response.statusText}`)
    }
    
    return await response.json()
  },

  async updatePharmacy(request: PharmacyUpdateRequest): Promise<Api.Resource> {
    const config = getApiConfiguration()
    const url = `${config.basePath}/pharmacy/${request.id}`
    
    const pharmacyData: Api.PharmacyPatchRequest = {
      name: request.name as Api.PharmacyPatchRequest['name'],
      address: request.address,
      phone: request.phone,
      email: request.email,
    }
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(pharmacyData)
    })
    
    if (!response.ok) {
      throw new Error(`Failed to update pharmacy: ${response.statusText}`)
    }
    
    return await response.json()
  },

  async deletePharmacy(id: string): Promise<void> {
    const config = getApiConfiguration()
    const url = `${config.basePath}/pharmacy/${id}`
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      }
    })
    
    if (!response.ok) {
      throw new Error(`Failed to delete pharmacy: ${response.statusText}`)
    }
  },

  // Pricing Management
  async updatePricing(request: PricingUpdateRequest): Promise<Api.Resource> {
    const config = getApiConfiguration()
    const url = `${config.basePath}/pharmacy/${request.pharmacyId}/formulary/${request.formularyId}`
    
    const pricingData: Api.PharmacyFormularyPutRequest = {
      price: request.price,
      currency: request.currency || 'USD',
    }
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(pricingData)
    })
    
    if (!response.ok) {
      throw new Error(`Failed to update pricing: ${response.statusText}`)
    }
    
    return await response.json()
  },

  async getPharmacyFormularies(pharmacyId: string): Promise<Api.PharmacyFormularyList> {
    const config = getApiConfiguration()
    const url = `${config.basePath}/pharmacy/${pharmacyId}/formulary`
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      }
    })
    
    if (!response.ok) {
      throw new Error(`Failed to get pharmacy formularies: ${response.statusText}`)
    }
    
    return await response.json()
  },

  async getFormularyPricing(pharmacyId: string, formularyId: string): Promise<Api.PharmacyFormularyGet200Response> {
    const config = getApiConfiguration()
    const url = `${config.basePath}/pharmacy/${pharmacyId}/formulary/${formularyId}`
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      }
    })
    
    if (!response.ok) {
      throw new Error(`Failed to get formulary pricing: ${response.statusText}`)
    }
    
    return await response.json()
  },

  async addPharmacyToFormulary(pharmacyId: string, formularyId: string, price: number): Promise<Api.Resource> {
    const config = getApiConfiguration()
    const url = `${config.basePath}/pharmacy/${pharmacyId}/formulary/${formularyId}`
    
    const pricingData: Api.PharmacyFormularyPutRequest = {
      price: price,
      currency: 'USD',
    }
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(pricingData)
    })
    
    if (!response.ok) {
      throw new Error(`Failed to add pharmacy to formulary: ${response.statusText}`)
    }
    
    return await response.json()
  },

  async removePharmacyFromFormulary(pharmacyId: string, formularyId: string): Promise<void> {
    const config = getApiConfiguration()
    const url = `${config.basePath}/pharmacy/${pharmacyId}/formulary/${formularyId}`
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${config.authorization}`,
        'Content-Type': 'application/json',
      }
    })
    
    if (!response.ok) {
      throw new Error(`Failed to remove pharmacy from formulary: ${response.statusText}`)
    }
  }
}

// Constants for form dropdowns
export const FORMULARY_CATEGORIES = [
  { value: 'peptide', label: 'Peptide' },
  { value: 'weight_loss', label: 'Weight Loss' },
  { value: 'hormone', label: 'Hormone' },
  { value: 'sexual_health', label: 'Sexual Health' },
  { value: 'nootropic', label: 'Nootropic' },
  { value: 'ldn', label: 'LDN' },
  { value: 'sterile', label: 'Sterile' },
  { value: 'anti_aging', label: 'Anti-Aging' },
  { value: 'topical_anesthetic', label: 'Topical Anesthetic' },
  { value: 'vitamin_replacement', label: 'Vitamin Replacement' }
]

export const FORMULARY_FORMS = [
  { value: 'injection', label: 'Injection' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'capsule', label: 'Capsule' },
  { value: 'cream', label: 'Cream' },
  { value: 'gel', label: 'Gel' },
  { value: 'liquid', label: 'Liquid' },
  { value: 'patch', label: 'Patch' }
]

export const FORMULARY_ROUTES = [
  { value: 'oral', label: 'Oral' },
  { value: 'injection', label: 'Injection' },
  { value: 'topical', label: 'Topical' },
  { value: 'nasal_spray', label: 'Nasal Spray' },
  { value: 'suppository', label: 'Suppository' },
  { value: 'transdermal', label: 'Transdermal' }
]

export const PHARMACY_NAMES = [
  { value: 'Beaker', label: 'Beaker' },
  { value: 'Partell', label: 'Partell' },
  { value: 'PerfectRx', label: 'PerfectRx' },
  { value: 'Promise', label: 'Promise' },
  { value: 'Striker', label: 'Striker' },
  { value: 'Strive', label: 'Strive' }
]